import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Divider, Form, Row } from 'antd'
import { includes, isNil, isUndefined } from 'lodash'
import PropTypes from 'prop-types'

import { getFilterOptions } from '../../../core/utils'

import {
  FormButtons,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import { StyledPaddingTopCol } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { useFormDirty } from '../../../common/hooks'

import { useUserHasPermissions } from '../../../account/hooks'
import {
  BRAND_CURATION_TASK_PROBLEM_REASON,
  BRAND_CURATION_TASK_TAGS,
} from '../../constants'
import { brandCurationTaskPropType } from '../../prop-types'

import { brandCurationTaskTagsFormInitialValues } from './initial-values'

const BrandCurationTaskTagsForm = ({
  brandCurationTask,
  isLoading,
  isDisabled,
  onSubmit,
}) => {
  const [selectedTags, setSelectedTags] = useState(null)
  const [selectedProblemReason, setSelectedProblemReason] = useState(null)
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)
  const initialValues = useMemo(
    () => brandCurationTaskTagsFormInitialValues(brandCurationTask),
    [brandCurationTask],
  )

  const hasPermissionToChangeTags = useUserHasPermissions(
    PERMISSIONS.BRAND_CURATION_TASK.UPDATE_TAGS,
  )

  useEffect(() => {
    resetForm()
    setSelectedTags(initialValues.tags)
    setSelectedProblemReason(initialValues.problemReason)
  }, [initialValues, resetForm, setSelectedTags])

  const tagsOptions = useMemo(
    () => getFilterOptions(BRAND_CURATION_TASK_TAGS, 'label', 'value'),
    [],
  )
  const problemReasonOptions = useMemo(
    () =>
      getFilterOptions(BRAND_CURATION_TASK_PROBLEM_REASON, 'label', 'value'),
    [],
  )

  const areTagsDisabled = useMemo(
    () => isDisabled || !hasPermissionToChangeTags,
    [hasPermissionToChangeTags, isDisabled],
  )

  const isProblemReasonDisabled = useMemo(
    () =>
      isDisabled ||
      !includes(selectedTags, BRAND_CURATION_TASK_TAGS.IS_PROBLEMATIC.value),
    [isDisabled, selectedTags],
  )

  const isProblemCommentDisabled = useMemo(
    () => isDisabled || isProblemReasonDisabled || isNil(selectedProblemReason),
    [isDisabled, isProblemReasonDisabled, selectedProblemReason],
  )

  const handleFormValuesChange = useCallback(changedValues => {
    if (!isUndefined(changedValues.tags)) {
      setSelectedTags(changedValues.tags)
    }
    if (!isUndefined(changedValues.problemReason)) {
      setSelectedProblemReason(changedValues.problemReason)
    }
  }, [])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="editBrandCurationTask"
      onFinish={onSubmit}
      autoComplete="off"
      onFieldsChange={setToDirty}
      onValuesChange={handleFormValuesChange}
    >
      <Row gutter={[16]}>
        <Col span={5}>
          <FormSelect
            mode="multiple"
            label="Tags"
            labelAbove
            showSearch
            allowClear
            name="tags"
            options={tagsOptions}
            placeholder="Select tags..."
            disabled={areTagsDisabled}
            showColon={false}
          />
        </Col>
        <Col span={3}>
          <FormSelect
            label="Problem reason"
            labelAbove
            showSearch
            allowClear
            name="problemReason"
            rules={[
              {
                required: !isProblemReasonDisabled,
                message: 'Please select a problem reason',
              },
            ]}
            options={problemReasonOptions}
            placeholder="Select a problem reason..."
            disabled={isProblemReasonDisabled}
            showColon={false}
          />
        </Col>
        <Col span={5}>
          <FormTextareaInput
            label="Problem comment"
            labelAbove
            name="problemComment"
            rules={[
              {
                required: !isProblemCommentDisabled,
                message: 'Please select a problem comment',
              },
            ]}
            placeholder="Select a problem comment..."
            disabled={isProblemCommentDisabled}
            showColon={false}
          />
        </Col>
        <StyledPaddingTopCol span={4}>
          <FormButtons
            permissions={[PERMISSIONS.BRAND_CURATION_TASK.UPDATE_TAGS]}
            isDisabled={!isDirty}
            isLoading={isLoading}
            onReset={resetForm}
          />
        </StyledPaddingTopCol>
      </Row>
      <Divider />
    </Form>
  )
}

BrandCurationTaskTagsForm.propTypes = {
  brandCurationTask: brandCurationTaskPropType.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
}

BrandCurationTaskTagsForm.defaultProps = {
  isLoading: false,
  isDisabled: false,
}

export default BrandCurationTaskTagsForm
